import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, Printer } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`).then(r => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const win = window.open("", "_blank", "width=400,height=600");
    win.document.write(`<html><head><title>Order ${orderId}</title>
      <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:16px;color:#000;font-size:13px}.header{text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:12px}.header h1{font-size:18px;margin-bottom:2px}.header p{font-size:11px;color:#444}.room-block{text-align:center;margin:12px 0}.room-block .room{font-size:36px;font-weight:bold}.room-block .label{font-size:11px;color:#666;text-transform:uppercase;letter-spacing:1px}.info-row{display:flex;justify-content:space-between;padding:3px 0}.info-row .label{color:#666}.info-row .value{font-weight:600}.divider{border-top:1px dashed #999;margin:10px 0}.items-table{width:100%;border-collapse:collapse;margin:8px 0}.items-table th{text-align:left;font-size:11px;color:#666;padding:4px 0;border-bottom:1px solid #ccc;text-transform:uppercase}.items-table td{padding:5px 0}.items-table .qty{text-align:center;width:30px}.items-table .price{text-align:right}.total-row{display:flex;justify-content:space-between;font-size:16px;font-weight:bold;padding:8px 0;border-top:2px solid #000;margin-top:4px}.footer{text-align:center;margin-top:16px;font-size:11px;color:#666}@media print{body{padding:0}}</style></head>
      <body>${printRef.current.innerHTML}</body></html>`);
    win.document.close(); win.focus(); win.print(); win.close();
  };

  return (
    <div className="min-h-screen bg-[#1c3b2e] flex flex-col">
      {/* Top branding */}
      <div className="pt-12 pb-6 text-center">
        <h1 className="text-3xl font-bold text-white tracking-tight">VOW</h1>
        <p className="text-[10px] font-medium tracking-[0.2em] text-[#bd9b5e] uppercase mt-1">Voyage of Wellness</p>
      </div>

      {/* Confirmation Card */}
      <div className="flex-1 bg-[#f5f1e6] rounded-t-3xl px-5 pt-8 pb-10">
        <div className="max-w-sm mx-auto text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-5">
            <div className="bg-[#1c3b2e]/10 p-4 rounded-full">
              <CheckCircle2 className="h-14 w-14 text-[#1c3b2e]" data-testid="success-icon" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#1c3b2e] mb-1">Order Placed!</h2>
          <p className="text-sm text-[#2c2c2c]/50 mb-6">Your meal is being prepared</p>

          {/* Order Details Card */}
          <div className="bg-white rounded-xl p-5 border border-[#1c3b2e]/5 text-left space-y-3 mb-5">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#2c2c2c]/40 uppercase tracking-wider">Order ID</span>
              <span className="font-bold text-sm text-[#1c3b2e]" data-testid="order-id">{orderId}</span>
            </div>
            {order && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#2c2c2c]/40">Room</span>
                  <span className="font-bold text-[#2c2c2c]" data-testid="confirmation-room">{order.room_number}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#2c2c2c]/40">Name</span>
                  <span className="font-semibold text-sm text-[#2c2c2c]" data-testid="confirmation-name">{order.patient_name}</span>
                </div>
                <div className="border-t border-[#1c3b2e]/5 pt-3">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-xs text-[#2c2c2c]/60 py-0.5">
                      <span>{item.name} × {item.quantity}</span>
                      <span>₹{(item.price * item.quantity).toFixed(0)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center border-t border-[#1c3b2e]/5 pt-3">
                  <span className="font-bold text-[#2c2c2c]">Total</span>
                  <span className="font-bold text-lg text-[#1c3b2e]" data-testid="confirmation-total">₹{order.total_amount?.toFixed(0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#2c2c2c]/40">Payment</span>
                  <span className="text-sm font-semibold text-[#2c2c2c] capitalize" data-testid="confirmation-payment">{order.payment_mode}</span>
                </div>
              </>
            )}
          </div>

          {/* Delivery Estimate */}
          <div className="bg-[#1c3b2e]/5 rounded-xl px-4 py-3 flex items-center justify-center gap-2 mb-5">
            <Clock className="h-4 w-4 text-[#1c3b2e]" />
            <span className="text-sm text-[#2c2c2c]/60">Estimated delivery:</span>
            <span className="text-sm font-bold text-[#1c3b2e]" data-testid="confirmation-delivery-time">45-60 min</span>
          </div>

          <p className="text-xs text-[#2c2c2c]/40 mb-4">Payment will be collected at delivery</p>

          {/* Kiosk reassurance */}
          {order && order.source === "kiosk" && (
            <div className="bg-[#bd9b5e]/10 rounded-xl px-4 py-3 border border-[#bd9b5e]/20 mb-5" data-testid="kiosk-reassurance">
              <p className="text-sm text-[#2c2c2c] font-medium">Our team will confirm your room before delivery.</p>
            </div>
          )}
          {(!order || order.source !== "kiosk") && <div className="mb-5" />}

          {/* Actions */}
          <div className="space-y-3">
            {order && (
              <button onClick={handlePrint}
                className="w-full border-2 border-[#1c3b2e]/15 text-[#1c3b2e] rounded-xl h-12 flex items-center justify-center gap-2 font-semibold text-sm hover:bg-[#1c3b2e]/5 transition-colors min-h-[48px]"
                data-testid="print-order-button">
                <Printer className="h-4 w-4" /> Print Order
              </button>
            )}
            <button onClick={() => navigate("/")}
              className="w-full bg-[#1c3b2e] text-white rounded-xl h-12 font-semibold text-sm hover:bg-[#1c3b2e]/90 transition-colors min-h-[48px]"
              data-testid="back-home-button">
              Back to Home
            </button>
          </div>

          <p className="text-[10px] text-[#2c2c2c]/30 mt-6">
            Need help? Contact the nurse station
          </p>
        </div>
      </div>

      {/* Hidden print content */}
      {order && (
        <div style={{ display: "none" }}>
          <div ref={printRef}>
            <div className="header"><h1>VOW In-Room Dining</h1><p>Hospital Food Service</p></div>
            <div className="room-block"><div className="label">Room Number</div><div className="room">{order.room_number}</div></div>
            <div className="divider"></div>
            <div className="info-row"><span className="label">Order ID</span><span className="value">{order.order_id}</span></div>
            <div className="info-row"><span className="label">Date</span><span className="value">{format(new Date(order.created_at), "dd MMM yyyy, hh:mm a")}</span></div>
            <div className="info-row"><span className="label">Name</span><span className="value">{order.patient_name}</span></div>
            <div className="info-row"><span className="label">Phone</span><span className="value">{order.phone_number}</span></div>
            <div className="info-row"><span className="label">Payment</span><span className="value" style={{textTransform:"capitalize"}}>{order.payment_mode}</span></div>
            <div className="divider"></div>
            <table className="items-table"><thead><tr><th>Item</th><th className="qty">Qty</th><th className="price">Amount</th></tr></thead>
              <tbody>{order.items.map((item, i) => (
                <tr key={i}><td>{item.name}</td><td className="qty">{item.quantity}</td><td className="price">₹{(item.price * item.quantity).toFixed(2)}</td></tr>
              ))}</tbody>
            </table>
            <div className="total-row"><span>Total</span><span>₹{order.total_amount?.toFixed(2)}</span></div>
            {order.special_instructions && (<><div className="divider"></div><p style={{fontSize:"11px",color:"#666"}}>Notes: {order.special_instructions}</p></>)}
            <div className="footer"><p>Estimated delivery: 45-60 minutes</p><p style={{marginTop:"4px"}}>Thank you!</p></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderConfirmation;
